import React from 'react';
import {
  Box, Text, VStack, HStack, Pressable, Icon,
} from '@gluestack-ui/themed';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trash2, PlusCircle } from 'lucide-react-native';
import FormField from '@/common/components/forms/FormField';
import ContentGroup from '@/common/components/ContentGroup';

const wordcountValidation = ({ min, max, message }: { min: number; max: number; message?: string }) => (
  (value: string) => {
    const wordcount = (value || '').trim().split(' ').length;
    const defaultMessage = `Word count must be between ${min} and ${max}`;
    const errorMessage = (message || defaultMessage).replace('{min}', String(min)).replace('{max}', String(max));
    if (wordcount < min || wordcount > max) {
      return errorMessage;
    }
    return true;
  }
);

interface LearningObjectiveFieldsProps {
  level: number;
  readonly: boolean;
}

const LearningObjectiveFields: React.FC<LearningObjectiveFieldsProps> = ({ level, readonly }) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `objectives.${level}`,
  });

  return (
    <VStack space="xs">
      {fields.map((field, fieldIndex) => (
        <HStack key={field.id} space="xs" alignItems="center">
          <Box flex={1}>
            <FormField
              type="text"
              fieldSize="xs"
              readonly={readonly}
              name={`objectives.${level}.${fieldIndex}`}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              defaultValue={(field as any).value}
              rules={{
                required: 'Learning objective must not be empty',
                validate: wordcountValidation({ min: 1, max: 20 }),
              }}
            />
          </Box>
          {!readonly && (
            <Pressable onPress={() => remove(fieldIndex)} p="$0.5" $hover={{ opacity: 0.7 }}>
              <Icon as={Trash2} size="xs" color="$textHint" />
            </Pressable>
          )}
        </HStack>
      ))}
      {fields.length < 3 && !readonly && (
        <Pressable mt="$1" onPress={() => append('')} flexDirection="row" alignItems="center" $hover={{ opacity: 0.7 }}>
          <Icon as={PlusCircle} size="xs" color="$textHint" />
        </Pressable>
      )}
    </VStack>
  );
};

interface LevelFormProps {
  level: number;
  readonly: boolean;
}

/**
 * LevelForm - Compact level editor using ContentGroup.
 * Reusable pattern with accent-colored left border.
 */
const LevelForm: React.FC<LevelFormProps> = ({ level, readonly }) => (
  <ContentGroup variant="accent" flex={1} p="$3">
    <VStack space="xs">
      <Text size="2xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md">
        LEVEL
        {' '}
        {level}
      </Text>

      <FormField
        name={`levelTitles.${level}`}
        type="text"
        placeholder="Title..."
        labelSize="xs"
        readonly={readonly}
        fieldSize="xs"
        errorTextSize="2xs"
        rules={{
          required: 'Level must have a title',
          minLength: { value: 2, message: 'Title must be at least 2 characters long' },
          maxLength: { value: 40, message: 'Title must be at most 40 characters long' },
          validate: wordcountValidation({
            min: 1,
            max: 5,
            message: 'Title must be between {min} and {max} words',
          }),
        }}
      />

      <Text size="2xs" color="$textHint" mt="$1" fontWeight="bold" textTransform="uppercase" letterSpacing="$md">
        Objectives
      </Text>
      <LearningObjectiveFields level={level} readonly={readonly} />
    </VStack>
  </ContentGroup>
);

export default LevelForm;
